import { useCallback, useEffect, useMemo, useState } from 'react';
import { useReactiveVar } from '@apollo/client';
import { useSearchParams } from 'react-router-dom';

import { useEcpStoresFromCache } from '../../graphql/ecp-locator/actions/ecp-stores';
import { filterParams, setUserLocation } from '../../graphql/ecp-locator/ecp-locator.cache';
import { ecpLocatorApi } from '../../rest/ecp-locator';
import { useSetFilterParams } from '../../graphql/ecp-locator/actions/set-filter-params';
import { useEcpUserLocation } from '../../graphql/ecp-locator/actions/user-location';
import { DEFAULT_RANGE } from '../../constants/ecp-locator';
import { filteringByStores, getServices } from '../../utils/ecp-locator';
import { EcpServices } from '../../components/business/app/ecp-locator';
import { SortingTypes } from '../../components/business/app/ecp-locator/ecp-sort/ecp-sort.models';
import {
  retailerShopTypeItems, shopTypeItems,
} from '../../components/business/app/ecp-filters-modal';
import { useLEGroupForFirstFitting } from '../use-le-group-for-first-fitting';
import { UpdateStores } from '../../models';

export const useUpdateEcpStores = () => {
  const { setEcpStoresToCache } = useEcpStoresFromCache();
  const { setFilterParamsToCache } = useSetFilterParams();
  const { setEcpUserLocationToCache } = useEcpUserLocation();
  const filtersCurrent = useReactiveVar(filterParams);
  const userLocationCurrent = useReactiveVar(setUserLocation);
  const [isStoresLoading, setLoadingState] = useState(false);
  const [isStoresError, setErrorState] = useState(false);
  const [searchParams] = useSearchParams();
  const ecpService = searchParams.get('ecpService') as EcpServices | null;
  const isCvServiceEnabled = ecpService === EcpServices.cv;
  const services = useMemo(() => {
    return getServices(ecpService);
  }, [ecpService]);
  const storeGroupCode = searchParams.get('storeGroupCode');

  const groupLECode = useLEGroupForFirstFitting(isCvServiceEnabled);

  const updateStores = useCallback(async ({ filters, userLocation }: UpdateStores) => {
    let filtersApplied = filtersCurrent;
    let userLocationApplied = userLocationCurrent;

    if (filters) {
      filtersApplied = filters;
    }

    if (userLocation) {
      userLocationApplied = userLocation;
    }

    const { specializations, shops, c2cActivated } = filtersApplied;
    const { latitude, longitude } = userLocationApplied;

    try {
      setLoadingState(true);
      const { data } = await ecpLocatorApi.getStores({
        lat: latitude,
        long: longitude,
        range: DEFAULT_RANGE,
        sorting: SortingTypes.distance,
        speciality: specializations.join(','),
        type: c2cActivated ? '' : services || '',
        c2cActivated: !!c2cActivated,
        isSurgeryClinic: ecpService === EcpServices.cataract,
        storeGroupCode,
      });

      if (filters) {
        setFilterParamsToCache(filters);
      }

      if (userLocation) {
        setEcpUserLocationToCache(userLocation);
      }

      const filteredByShop = filteringByStores(
        data,
        shops,
        (ecpService === EcpServices.buy ? retailerShopTypeItems.length : shopTypeItems.length),
      );
      const filteredByLeGroup = isCvServiceEnabled && groupLECode
        ? filteredByShop.filter((s) => s.storeGroupCode === groupLECode)
        : filteredByShop;
      setEcpStoresToCache(filteredByLeGroup);
    } catch {
      setErrorState(true);
    }
    setLoadingState(false);
  }, [
    filtersCurrent,
    userLocationCurrent,
    setEcpStoresToCache,
    setEcpUserLocationToCache,
    setFilterParamsToCache,
    isCvServiceEnabled,
    groupLECode,
    services,
    ecpService,
  ]);

  useEffect(() => () => {
    setLoadingState(false);
  }, []);

  return { updateStores, isStoresLoading, isStoresError };
};
